import React, { useState, useEffect, useCallback, useRef } from "react";
import { connect } from "react-redux";
import SimpleMessageDialog from "./SimpleMessageDialog";
import SimpleConfirmDialog from "./SimpleConfirmDialog";
import moment from "moment";
import {useIdleTimer} from "react-idle-timer";
import { userActions, appActions } from "../actions";
import userService from "../services/UserServices";
import { debounce } from "lodash";

const SessionExpireDialog = props => {
  const { user, refreshApp, logout, history, refreshToken } = props;

  const SESSION_PROMPT_TIME = 5;
  const IDLE_THRESHOLD = 10;
  const PROMPT_TIMEOUT = 4;

  const [open, setOpen] = useState(false);
  const [isIdle, setIsIdle] = useState(false);
  const [showExtendSessionPrompt, setShowExtendSessionPrompt] = useState(false);
  const [showFinalWarning, setShowFinalWarning] = useState(false);
  const promptTimerId = useRef(null);
  const hasPromptedRef = useRef(false);
  const [countdown, setCountdown] = useState(PROMPT_TIMEOUT * 60);
  const countdownIntervalRef = useRef(null);

  const getSessionEndTime = () =>
    user.auth?.expire_time ? moment(user.auth.expire_time) : null;

  const handleExtendSession = useCallback(() => {
    const sessionEndTime = getSessionEndTime();
    const now = moment();
    if(!sessionEndTime || now.isAfter(sessionEndTime)){
      setShowExtendSessionPrompt(false);
      setOpen(true);
      logout();
      return ;
    }

    refreshToken({
      "x-auth-random-key": user["x-auth-random-key"],
      "x-auth-tx-id": user["x-auth-tx-id"]
    })
      .then(() => {
        setShowExtendSessionPrompt(false);
        hasPromptedRef.current = false;

        if (promptTimerId.current) {
          clearTimeout(promptTimerId.current);
          promptTimerId.current = null;
        }
      })
      .catch(err => {
        if (err.message === "Refresh token max extension reached") {
          setShowFinalWarning(true);
        }else{
          setOpen(true)
        }
      });
  }, [refreshToken, user, logout]);

  const handleDeclineExtension = () => {
    setShowExtendSessionPrompt(false);
    setOpen(true);
  };

  const handleCloseReminderPopUp = () => {
    setShowExtendSessionPrompt(false);
    userService.revokeToken();
    logout();
    history.push("/");
  };

  const handleClose = () => {
    refreshApp();
    logout();
    window.location.href= "/";
  };

  const handleIdle = () => {
    setIsIdle(true);
  };

  const onAcitve = () =>{
    setIsIdle(false)
  }

  useIdleTimer({
    timeout: IDLE_THRESHOLD * 60 * 1000,
    // timeout: (1000  * 15),
    onIdle: handleIdle,
    onActive: onAcitve
  });

  //main session check effect 
  useEffect(() => {
    const sessionEndTime = getSessionEndTime();
    if (!sessionEndTime) return;

    const interval = setInterval(() => {
      const now = moment();
      const minutesLeft = sessionEndTime.diff(now, "minutes");
      console.log("minutesLeft", minutesLeft);
      console.log("now", now);
      if (sessionEndTime.isBefore(now)) {
        setOpen(true);
        return;
      }

      if (
        minutesLeft <= SESSION_PROMPT_TIME &&
        minutesLeft > 0 &&
        !hasPromptedRef.current
      ) {
        hasPromptedRef.current = true;
        if (isIdle) {
          setShowExtendSessionPrompt(true);
        } else {
          handleExtendSession();
        }
      }
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, [user.auth?.expire_time, isIdle, showFinalWarning, handleExtendSession]);

  useEffect(() => {
    if (showExtendSessionPrompt) {
      setCountdown(PROMPT_TIMEOUT * 60);
      countdownIntervalRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            handleDeclineExtension();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [showExtendSessionPrompt]);

  useEffect(() => {
    if (
      user.error &&
      user.error.code === 401 &&
      user.error.message.includes("Invalid token")
    ) {
      setOpen(true);
    }
  }, [user.error]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const sessionEndTime = getSessionEndTime();
        if (!sessionEndTime || moment().isAfter(sessionEndTime)) {
          setShowExtendSessionPrompt(false);
          setOpen(true);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [user.auth?.expire_time]);

  const sessionEndTime = getSessionEndTime();

  return (
    <>

      <SimpleConfirmDialog
        name="extend-session-dialog"
        title="You have been idle for 10 mins, would you like to keep the session active?"
        description={`Session will expire in ${Math.floor(
          countdown / 60
        )}:${String(countdown % 60).padStart(2, "0")}`}
        closeButtonText="No"
        okButtonText="Yes"
        closeHandler={handleCloseReminderPopUp}
        okHandler={handleExtendSession}
        open={showExtendSessionPrompt}
      />

      <SimpleMessageDialog
        type="none"
        name="final-warning"
        title="Session Ending Soon"
        description={`Please save / submit your works as you have reach max time allowed. Session will end on ${
          sessionEndTime ? moment(sessionEndTime).format("HH:mm") : ""
        }`}
        closeButtonText="Close"
        closeHandler={() => {
          setShowFinalWarning(false);
          setShowExtendSessionPrompt(false);
        }}
        open={showFinalWarning}
      />

      <SimpleMessageDialog
        type="none"
        name="timeout-dialog"
        title="Session Expired"
        description="Please login again."
        closeButtonText="OK"
        closeHandler={handleClose}
        open={open}
      />
    </>
  );
};

const mapStateToProps = state => ({
  user: state.user,
  app: state.app
});

const mapDispatchToProps = dispatch => ({
  refreshApp: () => dispatch(appActions.setError({})),
  logout: () => dispatch(userActions.logout()),
  refreshToken: params => dispatch(userActions.updateRefreshToken(params))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SessionExpireDialog);
